/**
 * LE PILOTAGE, CÔTÉ ÉCRAN — lot 148, 02/11/2026.
 *
 * Les chiffres viennent de `GET /pilotage` (`apps/api/src/pilotage.ts`), qui
 * regroupe le CA et le coût déjà calculés pour le Résultat. Ici on ne calcule
 * que des PÉRIODES et des FORMATS : aucun argent n'est recompté.
 */

import { appeler, echec, type Resultat } from './api.js';

export interface PilotageLu {
  readonly journees_comptees: number;
  readonly incompletes: number;
  readonly totaux: {
    readonly ca_centimes: number;
    readonly cout_centimes: number;
    readonly creux_centimes: number;
    readonly marge_centimes: number;
    readonly km: number;
    readonly km_vide: number;
  };
  readonly par_jour: readonly {
    readonly jour: string;
    readonly km: number;
    readonly km_vide: number;
    readonly ca_centimes: number;
    readonly cout_centimes: number;
  }[];
  readonly par_chauffeur: readonly {
    readonly chauffeur_id: string | null;
    readonly nom: string;
    readonly ca_centimes: number;
    readonly km: number;
    readonly cout_centimes: number;
    readonly marge_centimes: number;
  }[];
  readonly par_client: readonly {
    readonly client_id: string;
    readonly nom: string;
    readonly ca_centimes: number;
  }[];
}

export async function lirePilotage(
  jeton: string,
  du: string,
  au: string,
): Promise<Resultat<PilotageLu>> {
  const echange = await appeler(
    `/pilotage?du=${encodeURIComponent(du)}&au=${encodeURIComponent(au)}`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as PilotageLu };
}

// ---------------------------------------------------------------------------
// Les périodes
// ---------------------------------------------------------------------------

export type Raccourci = 'semaine' | 'mois' | 'mois_precedent';

export interface Periode {
  readonly du: string;
  readonly au: string;
}

const iso = (d: Date): string =>
  `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

/** Lit « AAAA-MM-JJ » en date UTC à midi : aucun fuseau ne la fait changer de jour. */
const lireJour = (jour: string): Date => new Date(`${jour}T12:00:00Z`);

/**
 * LA PÉRIODE D'UN RACCOURCI, autour du jour donné.
 *   - semaine : du lundi au dimanche qui contiennent ce jour ;
 *   - mois : le mois de ce jour ;
 *   - mois précédent : le mois d'avant.
 */
export function periodeDe(raccourci: Raccourci, jour: string): Periode {
  const d = lireJour(jour);
  if (raccourci === 'semaine') {
    const decalage = (d.getUTCDay() + 6) % 7; // lundi = 0
    const lundi = new Date(d);
    lundi.setUTCDate(d.getUTCDate() - decalage);
    const dimanche = new Date(lundi);
    dimanche.setUTCDate(lundi.getUTCDate() + 6);
    return { du: iso(lundi), au: iso(dimanche) };
  }
  const annee = d.getUTCFullYear();
  const mois = d.getUTCMonth() - (raccourci === 'mois_precedent' ? 1 : 0);
  const premier = new Date(Date.UTC(annee, mois, 1, 12));
  const dernier = new Date(Date.UTC(annee, mois + 1, 0, 12));
  return { du: iso(premier), au: iso(dernier) };
}

/** Le raccourci qui donne exactement cette période, s'il y en a un. */
export function raccourciDe(periode: Periode, jour: string): Raccourci | null {
  for (const r of ['semaine', 'mois', 'mois_precedent'] as const) {
    const p = periodeDe(r, jour);
    if (p.du === periode.du && p.au === periode.au) return r;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Les formats
// ---------------------------------------------------------------------------

const ENTIER = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

/** « 58 208 € », arrondi à l'euro. */
export function eurosRonds(centimes: number): string {
  return `${ENTIER.format(Math.round(centimes / 100))} €`;
}

/** « 12,8 % » de `part` sur `total` ; `—` sans total. */
export function pourcentage(part: number, total: number): string {
  if (total === 0) return '—';
  return `${(Math.round((part / total) * 1000) / 10).toLocaleString('fr-FR')} %`;
}

/** « 1,51 € » : coût au km ; `—` sans km. */
export function coutAuKm(centimes: number, km: number): string {
  if (km === 0) return '—';
  return `${(centimes / 100 / km).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export function kmDits(km: number): string {
  return ENTIER.format(km);
}

/** « 05/11 » */
export function jourCourt(jour: string): string {
  return `${jour.slice(8, 10)}/${jour.slice(5, 7)}`;
}

/**
 * LES CLIENTS AU-DELÀ DES SIX PREMIERS se regroupent en « Autres (n) » : un
 * classement de vingt lignes ne se lit plus. Maquette validée le 02/11.
 */
export function clientsResumes<T extends { readonly nom: string; readonly ca_centimes: number }>(
  clients: readonly T[],
  combien = 6,
): readonly { readonly nom: string; readonly ca_centimes: number }[] {
  if (clients.length <= combien + 1) return clients;
  const premiers = clients.slice(0, combien);
  const reste = clients.slice(combien);
  return [
    ...premiers,
    {
      nom: `Autres (${String(reste.length)})`,
      ca_centimes: reste.reduce((a, c) => a + c.ca_centimes, 0),
    },
  ];
}

/** Une graduation « ronde » au-dessus du maximum : 2 800 → 3 000. */
export function plafondRond(max: number): number {
  if (max <= 0) return 1;
  const puissance = 10 ** Math.floor(Math.log10(max));
  for (const m of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (m * puissance >= max) return m * puissance;
  }
  return 10 * puissance;
}
